import { useCallback, useState } from 'react'
import { getApiBaseUrl } from '@/app/config/settings'

export type MappingEntry = {
  id?: string | null
  safety_culture_label: string
  mapped?: boolean | null
  catalog_id?: string | null
  priority?: string | null
  target_flow?: string | null
  catalog_area?: string | null
  catalog_condition_type?: string | null
  catalog_extent?: string | null
  error_missing?: boolean
  error_multiple?: boolean
}

export type ListTemplateMappingsResponse = {
  entries: MappingEntry[]
}

export function useTemplateMappings(getFirebaseToken: () => Promise<string | null>) {
  const [data, setData] = useState<ListTemplateMappingsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const loadMappings = useCallback(async (templateId: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const token = await getFirebaseToken()
      const base = getApiBaseUrl()
      if (!base) {
        throw new Error('API base URL not configured')
      }
      const params = new URLSearchParams({ template_id: templateId })
      const response = await fetch(`${base}/safetyculture/templates/mappings?${params}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
      if (!response.ok) {
        throw new Error(`Failed to load mappings: ${response.status}`)
      }
      const json: ListTemplateMappingsResponse = await response.json()
      setData(json)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load mappings'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const patchEntry = useCallback((label: string, patch: Partial<MappingEntry>) => {
    setData((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        entries: prev.entries.map((e) =>
          e.safety_culture_label === label ? { ...e, ...patch } : e
        ),
      }
    })
  }, [])

  return { data, isLoading, error, loadMappings, patchEntry }
}
